import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Spinner,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import {
    getAstAssetCategories,
    getAstAssetNameCategoriesByAssetCategory,
    getAstAssetObservedConditions,
    getAstInstitutionCategories,
    getAstInstitutionsByCategory,
    getAstInstitutionBuildings,
    getBuildingUses,
    getWardsByCouncil,
    getAsset,
} from "../../services/ValitanApiService";

/* ======================================================
   HELPERS
====================================================== */
const v = (x) =>
    x === null || x === undefined || x === "" ? "" : String(x);

const toStringValue = (x) =>
    x === null || x === undefined ? "" : String(x);

const toIntOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? null : n;
};

const firstDefinedValue = (source, keys = []) => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== null && value !== undefined) {
            return value;
        }
    }
    return "";
};

const getEntityLabel = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object") {
        return String(
            value.name ||
            value.label ||
            value.code ||
            value.display_name ||
            value.displayName ||
            ""
        );
    }
    return "";
};

const normalizeResponseList = (response) => {
    const data = response?.data?.data;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    return [];
};

const normalizeResponseItem = (response) => {
    if (!response) return null;
    if (response.data?.data) return response.data.data;
    if (response.data) return response.data;
    return response;
};

const _isSchoolCategory = (code = "") =>
    code.toUpperCase().includes("SCHOOL");

const healthFacilityRooms = [
    { label: "Injection room", value: "INJECTION_ROOM" },
    { label: "CTC", value: "CTC" },
    { label: "Theatre", value: "THEATRE" },
    { label: "Recovery room", value: "RECOVERY_ROOM" },
    { label: "Vaccine room", value: "VACCINE_ROOM" },
    { label: "Doctor's room", value: "DOCTORS_ROOM" },
    { label: "Pharmacy", value: "PHARMACY" },
    { label: "Dispensing room", value: "DISPENSING_ROOM" },
    { label: "Sluice room", value: "SLUICE_ROOM" },
    { label: "Doctor incharge room", value: "DOCTOR_INCHARGE_ROOM" },
    { label: "Nurse incharge room", value: "NURSE_INCHARGE_ROOM" },
    { label: "Reception room", value: "RECEPTION_ROOM" },
    { label: "Conference hall", value: "CONFERENCE_HALL" },
    { label: "Dental room", value: "DENTAL_ROOM" },
    { label: "Dental department room", value: "DENTAL_DEPARTMENT_ROOM" },
    { label: "Ultrasound room", value: "ULTRASOUND_ROOM" },
    { label: "Triage room", value: "TRIAGE_ROOM" },
    { label: "Dressing room", value: "DRESSING_ROOM" },
    { label: "Children rest room", value: "CHILDREN_REST_ROOM" },
    { label: "Women rest room", value: "WOMEN_REST_ROOM" },
    { label: "Vaccine hall", value: "VACCINE_HALL" },
    { label: "Human resource room", value: "HUMAN_RESOURCE_ROOM" },
    { label: "Control room", value: "CONTROL_ROOM" },
    { label: "NCU room", value: "NCU_ROOM" },
    { label: "Private ward room", value: "PRIVATE_WARD_ROOM" },
    { label: "Internal ward room", value: "INTERNAL_WARD_ROOM" },
    { label: "TB room", value: "TB_ROOM" },
    { label: "Women ward room", value: "WOMEN_WARD_ROOM" },
    { label: "Family plan room", value: "FAMILY_PLAN_ROOM" },
    { label: "RCH room", value: "RCH_ROOM" },
    { label: "Accountant room", value: "ACCOUNTANT_ROOM" },
    { label: "Observation room", value: "OBSERVATION_ROOM" },
    { label: "Operating room", value: "OPERATING_ROOM" },
    { label: "Changing room", value: "CHANGING_ROOM" },
    { label: "Laboratory", value: "LABORATORY" },
    { label: "Laundry room", value: "LAUNDRY_ROOM" },
    { label: "Maternity room", value: "MATERNITY_ROOM" },
    { label: "ANC room", value: "ANC_ROOM" },
    { label: "Admission room", value: "ADMISSION_ROOM" },
    { label: "Preparation room", value: "PREPARATION_ROOM" },
    { label: "Cash point room", value: "CASH_POINT_ROOM" },
    { label: "Others (Specify)", value: "OTHERS" },
];

const councilHqRooms = [
    { label: "Human resources", value: "HR_ROOM" },
    { label: "Health secretary", value: "HEALTH_SECRETARY" },
    { label: "Health department", value: "HEALTH_DEPT" },
    { label: "Valuation office", value: "VALUATION_OFFICE" },
    { label: "Public service and administration", value: "PUBLIC_SERVICE_ADMIN" },
    { label: "Environment and forestry", value: "ENVIRONMENT_FORESTRY" },
    { label: "Finance and trade", value: "FINANCE_TRADE" },
    { label: "Revenue office", value: "REVENUE_OFFICE" },
    { label: "Agriculture, livestock and fisheries", value: "AGRICULTURE_LIVESTOCK" },
    { label: "Treasurer", value: "TREASURER" },
    { label: "Communications office", value: "COMMUNICATIONS" },
    { label: "Construction department", value: "CONSTRUCTION_DEPT" },
    { label: "Community development", value: "COMMUNITY_DEVELOPMENT" },
    { label: "Economist office", value: "ECONOMIST" },
    { label: "TASAF office", value: "TASAF" },
    { label: "Urban planning", value: "URBAN_PLANNING" },
    { label: "Culture and sports", value: "CULTURE_SPORTS" },
    { label: "Head of lands department", value: "HEAD_LANDS_DEPT" },
    { label: "ICT unit", value: "ICT_UNIT" },
    { label: "Land surveyor", value: "LAND_SURVEYOR" },
    { label: "Registry", value: "REGISTRY" },
    { label: "Legal officer", value: "LEGAL_OFFICER" },
    { label: "Mayor's office", value: "MAYOR_OFFICE" },
    { label: "Director's office", value: "DIRECTOR_OFFICE" },
    { label: "Education department", value: "EDUCATION_DEPT" },
    { label: "Chief nurse office", value: "CHIEF_NURSE" },
    { label: "Others (Specify)", value: "OTHERS" },
];

const wardExecutiveRooms = [
    { label: "W.E.O office", value: "WEO_OFFICE" },
    { label: "M.E.O office", value: "MEO_OFFICE" },
    { label: "Others (Specify)", value: "OTHERS" },
];

const schoolRooms = [
    { label: "Headmaster office", value: "HEADMASTER_OFFICE" },
    { label: "Head teacher office", value: "HEAD_TEACHER_OFFICE" },
    { label: "Deputy headmaster office", value: "DEPUTY_HEADMASTER_OFFICE" },
    { label: "Deputy head teacher office", value: "DEPUTY_HEAD_TEACHER_OFFICE" },
    { label: "Academic master office", value: "ACADEMIC_MASTER_OFFICE" },
    { label: "Academic teacher office", value: "ACADEMIC_TEACHER_OFFICE" },
    { label: "Discipline master office", value: "DISCIPLINE_MASTER_OFFICE" },
    { label: "Discipline teacher office", value: "DISCIPLINE_TEACHER_OFFICE" },
    { label: "Staff room", value: "STAFF_ROOM" },
    { label: "Laboratory", value: "LABORATORY" },
    { label: "Library", value: "LIBRARY" },
    { label: "Computer lab", value: "COMPUTER_LAB" },
    { label: "Kitchen", value: "KITCHEN" },
    { label: "Store room", value: "STORE_ROOM" },
    { label: "Toilet", value: "TOILET" },
    { label: "Classroom", value: "CLASSROOM" },
    { label: "Admin block", value: "ADMIN_BLOCK" },
    { label: "Teachers quarters", value: "TEACHERS_QUARTERS" },
    { label: "Dining hall", value: "DINING_HALL" },
    { label: "Assembly hall", value: "ASSEMBLY_HALL" },
    { label: "Others (Specify)", value: "OTHERS" },
];

const getRoomListForCategory = (code = "") => {
    const upper = (code || "").toUpperCase();
    if (!upper) return [];
    if (upper === "HEALTH_FACILITIES") return healthFacilityRooms;
    if (upper === "COUNCIL_HQ") return councilHqRooms;
    if (upper === "WARD_HQ") return wardExecutiveRooms;
    if (_isSchoolCategory(upper)) return schoolRooms;
    return [];
};

const YEAR_OPTIONS = (() => {
    const currentYear = new Date().getFullYear();
    const startYear = 1900;
    const length = currentYear - startYear + 1;
    return Array.from({ length }, (_, index) =>
        String(currentYear - index)
    );
})();

/* ======================================================
   COMPONENT
====================================================== */
const AssetDetailForm = ({ asset, onChange, projectCouncilId, isLocked = false }) => {
    const [formValues, setFormValues] = useState(asset || {});
    const [assetNames, setAssetNames] = useState([]);
    const [observedConditions, setObservedConditions] = useState([]);
    const [assetCategories, setAssetCategories] = useState([]);
    const [institutionCategories, setInstitutionCategories] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [buildingUses, setBuildingUses] = useState([]);
    const [buildingsLoading, setBuildingsLoading] = useState(false);
    const [wards, setWards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAssetDetail, setLoadingAssetDetail] = useState(false);
    const [isAssetCategoryEditable, setIsAssetCategoryEditable] = useState(false);
    const [isAssetCategoryConfirmOpen, setIsAssetCategoryConfirmOpen] = useState(false);

    const isUserEditRef = useRef(false);

    useEffect(() => {
        isUserEditRef.current = false;
        setFormValues(asset || {});
        setIsAssetCategoryEditable(false);
        setIsAssetCategoryConfirmOpen(false);
    }, [asset]);

    const assetId = asset?.id;

    useEffect(() => {
        if (!assetId) {
            setLoadingAssetDetail(false);
            return;
        }

        let isActive = true;
        setLoadingAssetDetail(true);

        getAsset(assetId)
            .then((response) => {
                const detailed = normalizeResponseItem(response);
                if (isActive && detailed) {
                    isUserEditRef.current = false;
                    setFormValues((prev) => ({
                        ...(prev || {}),
                        ...detailed,
                    }));
                }
            })
            .catch((error) => {
                console.error("Unable to fetch asset detail", error);
            })
            .finally(() => {
                if (isActive) setLoadingAssetDetail(false);
            });

        return () => { isActive = false; };
    }, [assetId]);

    useEffect(() => {
        if (!isUserEditRef.current) return;
        onChange?.(formValues);
        isUserEditRef.current = false;
    }, [formValues, onChange]);

    const updateField = useCallback((field, value) => {
        isUserEditRef.current = true;
        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleAssetCategoryChange = useCallback((nextCategoryId) => {
        const selectedCategory = assetCategories.find(
            (category) => String(category.id) === String(nextCategoryId)
        );

        isUserEditRef.current = true;
        setFormValues((prev) => ({
            ...prev,
            asset_category_id: nextCategoryId || "",
            assetCategoryId: nextCategoryId || "",
            asset_category: selectedCategory || null,
            assetCategory: selectedCategory || null,
            asset_category_code: selectedCategory?.code || "",
            category_code: selectedCategory?.code || "",
            name: "",
            other_name: "",
            otherName: "",
        }));
    }, [assetCategories]);

    const renderInputField = (
        field,
        label,
        { md = 4, type = "text", placeholder, readOnly = false, sourceFields, writeFields } = {}
    ) => {
        const normalizedSourceFields = sourceFields || [field];
        const normalizedWriteFields = writeFields || [field];
        const inputValue = toStringValue(firstDefinedValue(formValues, normalizedSourceFields));

        const handleChange = (event) => {
            const { value } = event.target;
            const nextValue = type === "number" ? (value === "" ? null : Number(value)) : value;

            normalizedWriteFields.forEach((targetField) => {
                updateField(targetField, nextValue);
            });
        };

        return (
            <Col md={md}>
                <FormGroup>
                    <Label>{label}</Label>
                    <Input
                        type={type}
                        value={inputValue}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        onChange={readOnly ? undefined : handleChange}
                    />
                </FormGroup>
            </Col>
        );
    };

    const renderBooleanField = (field, label, { md = 4 } = {}) => {
        const camelField = field.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
        const rawValue = firstDefinedValue(formValues, [field, camelField]);
        const currentValue =
            rawValue === true ? "true" :
                rawValue === false ? "false" : "";

        const handleChange = (event) => {
            const nextValue = event.target.value;
            updateField(field, nextValue === "" ? null : nextValue === "true");
        };

        return (
            <Col md={md}>
                <FormGroup>
                    <Label>{label}</Label>
                    <Input type="select" value={currentValue} onChange={handleChange}>
                        <option value="">Select</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </Input>
                </FormGroup>
            </Col>
        );
    };

    // ────────────────────────────────────────────────────────────────
    //  Category / Name logic (kept mostly unchanged)
    // ────────────────────────────────────────────────────────────────
    const assetCategoryId = toStringValue(
        formValues.asset_category?.id || formValues.asset_category_id
    );

    const selectedAssetCategory = assetCategories.find(
        (c) => String(c.id) === assetCategoryId
    );

    const assetCategoryName = selectedAssetCategory?.name ||
        formValues.asset_category?.name ||
        formValues.category?.name || "";

    const assetCategoryCode = selectedAssetCategory?.code ||
        formValues.asset_category?.code ||
        formValues.asset_category_code ||
        formValues.category?.code || "";

    const normalizeAssetName = (value) => String(value || "").trim().toLowerCase();

    const assetNameOptions = useMemo(
        () => [
            ...assetNames,
            { id: "OTHERS", name: "OTHERS", code: "OTHERS" },
        ],
        [assetNames]
    );

    const findMatchingAssetOption = useCallback(
        (value) => {
            const target = normalizeAssetName(value);
            if (!target) return null;
            return assetNameOptions.find((option) => {
                const optionCode = normalizeAssetName(option.code);
                const optionName = normalizeAssetName(option.name);
                return optionCode === target || optionName === target;
            });
        },
        [assetNameOptions]
    );

    const assetNameSelectValue = useMemo(() => {
        if (formValues.name) {
            const match = findMatchingAssetOption(formValues.name);
            if (match) return match.code;
        }
        if (formValues.other_name) {
            const match = findMatchingAssetOption(formValues.other_name);
            if (match) return match.code;
            return "OTHERS";
        }
        return formValues.name || "";
    }, [formValues.name, formValues.other_name, findMatchingAssetOption]);

    useEffect(() => {
        if (!assetNameSelectValue) return;
        if (formValues.name === assetNameSelectValue) return;
        updateField("name", assetNameSelectValue);
    }, [assetNameSelectValue, formValues.name, updateField]);

    const acquisitionYearValue = toStringValue(
        firstDefinedValue(formValues, ["acquisition_year", "acquisition_date", "acquisitionDate"])
    );

    const normalizedCategoryCode = String(assetCategoryCode || "").toUpperCase();
    const isBuildingCategory = normalizedCategoryCode === "BLDG";
    const isLandCategory = normalizedCategoryCode === "LAND";
    const isSiteCategory = normalizedCategoryCode === "SITE";
    const isVehicleCategory = normalizedCategoryCode === "VEH";
    const isEquipmentCategory = normalizedCategoryCode === "EQP";
    const isICTCategory = normalizedCategoryCode === "ICT";
    const isMachineryCategory = normalizedCategoryCode === "MACH";
    const isInfrastructureCategory = normalizedCategoryCode === "INFR";

    // ────────────────────────────────────────────────────────────────
    //  Institution / Ward / Building logic
    // ────────────────────────────────────────────────────────────────
    const institutionCategoryCode = formValues.institution_category?.code ||
        formValues.institution_category_code ||
        formValues.institution_category ||
        formValues.institutionCategory || "";

    const institutionCategoryId = toStringValue(
        formValues.institution_category?.id || formValues.institution_category_id
    );

    const getInstitutionCategoryOptionValue = (c) => c?.code || String(c?.id || "");

    const selectedInstitutionCategory = institutionCategories.find(
        (c) =>
            c.code === institutionCategoryCode ||
            String(c.id) === institutionCategoryCode ||
            c.name === institutionCategoryCode
    ) || institutionCategories.find(
        (c) => String(c.id) === institutionCategoryId
    );

    const institutionCategoryValue = selectedInstitutionCategory?.code ||
        String(selectedInstitutionCategory?.id || "") ||
        institutionCategoryCode;

    const institutionCategoryParam = selectedInstitutionCategory?.id || "";

    const institutionId = toStringValue(
        formValues.institution?.id ||
        formValues.institution_id ||
        formValues.institutionId
    );

    const councilIdFromAsset = formValues.project?.council?.id ||
        formValues.project?.council_id ||
        formValues.project?.councilId || "";

    const effectiveCouncilId = projectCouncilId || councilIdFromAsset;

    const wardId = toStringValue(formValues.ward?.id || formValues.ward_id || formValues.wardId);

    const selectedInstitutionCategoryCode = selectedInstitutionCategory?.code ||
        formValues.institution_category?.code ||
        formValues.institution_category_code || "";

    const roomOptions = useMemo(
        () => getRoomListForCategory(selectedInstitutionCategoryCode),
        [selectedInstitutionCategoryCode]
    );

    const buildingNameValue = toStringValue(
        firstDefinedValue(formValues, ["building_name", "buildingName"])
    );
    const isBuildingOthers = buildingNameValue === "OTHERS";
    const hasBuildingList = buildings.length > 0;

    const roomValue = toStringValue(
        firstDefinedValue(formValues, ["room_name_or_number", "roomNameOrNumber"])
    );
    const isRoomOthers = roomValue === "OTHERS";

    const hideBuildingSection = ["BLDG", "LAND", "VEH", "INFR"].includes(normalizedCategoryCode);
    const shouldShowBuildingSection = !hideBuildingSection;

    const hasInstitution =
        Boolean(institutionId) ||
        Boolean(formValues.institution_other) ||
        Boolean(formValues.institutionOther) ||
        Boolean(formValues.institution_name) ||
        Boolean(formValues.institution?.name);

    const institutionCategoryOtherValue = toStringValue(
        firstDefinedValue(formValues, ["institution_category_other", "institutionCategoryOther"])
    );
    const institutionOtherValue = toStringValue(
        firstDefinedValue(formValues, ["institution_other", "institutionOther"])
    );
    const remarkValue = toStringValue(
        firstDefinedValue(formValues, ["remark", "remarks", "comment"])
    );
    const buildingUseValue = toStringValue(
        firstDefinedValue(formValues, ["building_use_name", "buildingUseName"]) ||
        getEntityLabel(formValues.building_use) ||
        getEntityLabel(formValues.buildingUse)
    );
    const buildingUseIdValue = toStringValue(
        firstDefinedValue(formValues, [
            "building_use_id",
            "buildingUseId",
        ]) || formValues.building_use?.id || formValues.buildingUse?.id
    );
    const savedInstitutionId = firstDefinedValue(formValues, [
        "institution_id",
        "institutionId",
    ]);
    const savedInstitutionCode = firstDefinedValue(formValues, [
        "institution_code",
        "institutionCode",
        "institution_name",
        "institutionName",
    ]);
    const savedInstitutionName = firstDefinedValue(formValues, [
        "institution_name",
        "institutionName",
    ]);
    const observedConditionValue = toStringValue(
        firstDefinedValue(formValues, ["observed_condition", "observedCondition"])
    );
    const isInstitutionCategoryOthers = String(institutionCategoryValue || "").toUpperCase() === "OTHERS";

    // ────────────────────────────────────────────────────────────────
    //  Data fetching (unchanged)
    // ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!assetCategoryId) {
            setAssetNames([]);
            return;
        }
        setLoading(true);
        getAstAssetNameCategoriesByAssetCategory(assetCategoryId)
            .then((r) => setAssetNames(normalizeResponseList(r)))
            .finally(() => setLoading(false));
    }, [assetCategoryId]);

    useEffect(() => {
        getAstAssetCategories().then((r) => setAssetCategories(normalizeResponseList(r)));
        getAstAssetObservedConditions().then((r) => setObservedConditions(normalizeResponseList(r)));
        getAstInstitutionCategories().then((r) => setInstitutionCategories(normalizeResponseList(r)));
        getBuildingUses().then((r) => setBuildingUses(normalizeResponseList(r)));
    }, []);

    useEffect(() => {
        if (!formValues.name || !assetNames.length) return;
        const hasMatch = assetNames.some((a) => a.code === formValues.name);
        if (hasMatch) {
            setFormValues((prev) => ({ ...prev, name: prev.name }));
        }
    }, [assetNames, formValues.name]);

    useEffect(() => {
        if (!institutionCategoryParam) {
            setInstitutions([]);
            return;
        }
        setLoading(true);
        getAstInstitutionsByCategory(institutionCategoryParam)
            .then((r) => setInstitutions(normalizeResponseList(r)))
            .finally(() => setLoading(false));
    }, [institutionCategoryParam]);

    useEffect(() => {
        if (!institutions.length) return;
        if (institutionId) return;

        const match =
            institutions.find((inst) => toStringValue(inst.id) === toStringValue(savedInstitutionId)) ||
            institutions.find((inst) => inst.code && savedInstitutionCode && inst.code === savedInstitutionCode) ||
            institutions.find((inst) => inst.name && savedInstitutionName && inst.name === savedInstitutionName);

        if (match) {
            updateField("institution_id", match.id || "");
            updateField("institution_name", match.name || "");
        }
    }, [
        institutions,
        institutionId,
        savedInstitutionId,
        savedInstitutionCode,
        savedInstitutionName,
        updateField,
    ]);

    useEffect(() => {
        if (!institutionId) {
            setBuildings([]);
            setBuildingsLoading(false);
            return;
        }
        setBuildingsLoading(true);
        getAstInstitutionBuildings(institutionId)
            .then((r) => setBuildings(normalizeResponseList(r)))
            .catch(() => setBuildings([]))
            .finally(() => setBuildingsLoading(false));
    }, [institutionId]);

    useEffect(() => {
        if (!effectiveCouncilId) {
            setWards([]);
            return;
        }
        let isActive = true;
        getWardsByCouncil(effectiveCouncilId)
            .then((r) => { if (isActive) setWards(normalizeResponseList(r)); })
            .catch(() => { if (isActive) setWards([]); });
        return () => { isActive = false; };
    }, [effectiveCouncilId]);

    // ────────────────────────────────────────────────────────────────
    //  RENDER
    // ────────────────────────────────────────────────────────────────
    return (
        <fieldset disabled={isLocked} style={{ margin: 0, padding: 0, border: 0 }}>
        <div>
            <h6 className="text-uppercase text-muted mb-3">General Information</h6>

            {(loading || loadingAssetDetail) && (
                <div className="text-center mb-3">
                    <Spinner size="sm" />
                </div>
            )}

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Asset Category *</Label>
                        <div className="d-flex gap-2 align-items-start">
                            {isAssetCategoryEditable ? (
                                <Input
                                    type="select"
                                    value={assetCategoryId}
                                    onChange={(e) => handleAssetCategoryChange(e.target.value)}
                                >
                                    <option value="">Select asset category</option>
                                    {assetCategories.map((category) => (
                                        <option key={category.id} value={String(category.id)}>
                                            {category.name}
                                        </option>
                                    ))}
                                </Input>
                            ) : (
                                <Input readOnly value={v(assetCategoryName)} />
                            )}

                            {!isAssetCategoryEditable && (
                                <Button
                                    type="button"
                                    color="primary"
                                    size="sm"
                                    className="flex-shrink-0"
                                    onClick={() => setIsAssetCategoryConfirmOpen(true)}
                                    title="Change asset category"
                                >
                                    <i className="ri-pencil-line me-1" />
                                    Change
                                </Button>
                            )}
                        </div>
                        <Input type="hidden" value={assetCategoryId} />
                    </FormGroup>
                </Col>

                <Col md={6}>
                    <FormGroup>
                        <Label>Asset Name *</Label>
                        <Input
                            type="select"
                            value={assetNameSelectValue}
                            onChange={(e) => updateField("name", e.target.value)}
                        >
                            <option value="">Choose asset</option>
                            {assetNames.map((a) => (
                                <option key={a.id} value={a.code}>
                                    {a.name}
                                </option>
                            ))}
                            <option value="OTHERS">OTHERS</option>
                        </Input>

                        {assetNameSelectValue === "OTHERS" && (
                            <Input
                                className="mt-2"
                                value={v(firstDefinedValue(formValues, ["other_name", "otherName"]))}
                                onChange={(e) =>
                                    ["other_name", "otherName"].forEach((f) =>
                                        updateField(f, e.target.value)
                                    )
                                }
                                placeholder="Specify asset name"
                            />
                        )}
                    </FormGroup>
                </Col>
            </Row>

            {!isBuildingCategory && (
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Quantity</Label>
                            <Input
                                type="number"
                                value={v(formValues.quantity)}
                                onChange={(e) =>
                                    updateField("quantity", e.target.value === "" ? null : Number(e.target.value))
                                }
                            />
                        </FormGroup>
                    </Col>
                </Row>
            )}

            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label>Description</Label>
                        <Input
                            type="textarea"
                            rows={2}
                            value={v(formValues.description)}
                            onChange={(e) => updateField("description", e.target.value)}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <h6 className="text-uppercase text-muted mt-4 mb-3">Location</h6>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>Ward</Label>
                        <Input
                            type="select"
                            value={wardId}
                            onChange={(e) => {
                                const val = e.target.value;
                                const selected = wards.find((w) => String(w.id) === val);
                                isUserEditRef.current = true;
                                setFormValues((prev) => ({
                                    ...prev,
                                    ward_id: val,
                                    wardId: val,
                                    ward: selected || null,
                                }));
                            }}
                        >
                            <option value="">Select ward</option>
                            {wards.map((w) => (
                                <option key={w.id} value={String(w.id)}>
                                    {w.name}
                                </option>
                            ))}
                            {wardId && !wards.some((w) => String(w.id) === wardId) && (
                                <option value={wardId}>{wardId}</option>
                            )}
                        </Input>
                    </FormGroup>
                </Col>

                <Col md={4}>
                    <FormGroup>
                        <Label>Institution Category</Label>
                        <Input
                            type="select"
                            value={institutionCategoryValue}
                            onChange={(e) => {
                                const selected = institutionCategories.find(
                                    (c) => getInstitutionCategoryOptionValue(c) === e.target.value
                                );
                                const selectedCode =
                                    e.target.value === "OTHERS"
                                        ? "OTHERS"
                                        : selected?.code || "";
                                updateField("institution_category_id", selected?.id || "");
                                updateField("institution_category_code", selectedCode);
                                updateField(
                                    "institution_category",
                                    selectedCode === "OTHERS" ? "OTHERS" : selected?.name || ""
                                );
                                updateField("institutionCategory", selectedCode);
                                updateField("institution_id", "");
                                updateField("institution_name", "");
                                updateField("institution_other", "");
                                updateField("institutionOther", "");
                                updateField("institution_category_other", "");
                                updateField("institutionCategoryOther", "");
                                updateField("building_name", "");
                                updateField("other_building_name", "");
                                updateField("floor", "");
                                updateField("room_name_or_number", "");
                                updateField("room_others", "");
                                setBuildings([]);
                            }}
                        >
                            <option value="">Select category</option>
                            {institutionCategories.map((c) => (
                                <option key={c.id} value={getInstitutionCategoryOptionValue(c)}>
                                    {c.name}
                                </option>
                            ))}
                            <option value="OTHERS">Others (Specify)</option>
                            {institutionCategoryValue &&
                                !institutionCategories.some(
                                    (c) => getInstitutionCategoryOptionValue(c) === institutionCategoryValue
                                ) &&
                                institutionCategoryValue !== "OTHERS" && (
                                    <option value={institutionCategoryValue}>
                                        {formValues.institution_category || institutionCategoryValue}
                                    </option>
                                )}
                        </Input>
                    </FormGroup>
                </Col>

                <Col md={4}>
                    <FormGroup>
                        <Label>Institution</Label>
                        {isInstitutionCategoryOthers || institutions.length === 0 ? (
                            <Input
                                value={institutionOtherValue}
                                onChange={(e) => {
                                    ["institution_other", "institutionOther", "institution_name"].forEach(
                                        (f) => updateField(f, e.target.value)
                                    );
                                }}
                                placeholder="Enter institution name"
                            />
                        ) : (
                            <Input
                                type="select"
                                value={institutionId}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const selected = institutions.find(
                                        (i) => String(i.id) === val
                                    );
                                    isUserEditRef.current = true;
                                    setFormValues((prev) => ({
                                        ...prev,
                                        institution_id: selected?.id || "",
                                        institutionId: selected?.id || "",
                                        institution: selected || null,
                                        institution_name: selected?.name || "",
                                        institution_other: "",
                                        institutionOther: "",
                                        building_name: "",
                                        other_building_name: "",
                                        floor: "",
                                        room_name_or_number: "",
                                        room_others: "",
                                    }));
                                    setBuildings([]);
                                }}
                            >
                                <option value="">Select institution</option>
                                {institutions.map((i) => (
                                    <option key={i.id} value={String(i.id)}>
                                        {i.name}
                                    </option>
                                ))}
                            </Input>
                        )}
                    </FormGroup>
                </Col>
            </Row>

            {isInstitutionCategoryOthers && (
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Specify Category</Label>
                            <Input
                                value={institutionCategoryOtherValue}
                                onChange={(e) =>
                                    ["institution_category_other", "institutionCategoryOther"].forEach(
                                        (f) => updateField(f, e.target.value)
                                    )
                                }
                                placeholder="Enter category"
                            />
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {/* ─── BUILDING & ROOM ── placed right after Institution ─────── */}
            {shouldShowBuildingSection && hasInstitution && (
                <>
                    <h6 className="text-uppercase text-muted mt-4 mb-2 small">Building & Room Details</h6>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Building Name (optional)</Label>
                                {buildingsLoading ? (
                                    <div className="small text-muted">Loading buildings...</div>
                                ) : !hasBuildingList ? (
                                    <Input
                                        value={buildingNameValue}
                                        onChange={(e) => {
                                            updateField("building_name", e.target.value);
                                            updateField("other_building_name", "");
                                        }}
                                        placeholder="Type building name"
                                    />
                                ) : (
                                    <Input
                                        type="select"
                                        value={buildingNameValue || ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            updateField("building_name", val);
                                            if (val !== "OTHERS") updateField("other_building_name", "");
                                            updateField("room_name_or_number", "");
                                            updateField("room_others", "");
                                        }}
                                    >
                                        <option value="">Select building</option>
                                        {buildings
                                            .map((b) => {
                                                const name = b?.name?.trim();
                                                if (!name) return null;
                                                return { label: name || "Unnamed Building", value: name };
                                            })
                                            .filter(Boolean)
                                            .map((item) => (
                                                <option key={item.value} value={item.value}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        <option value="OTHERS">Others (Specify)</option>
                                    </Input>
                                )}
                            </FormGroup>
                        </Col>

                        <Col md={3}>
                            <FormGroup>
                                <Label>Floor (optional)</Label>
                                <Input
                                    value={v(formValues.floor)}
                                    onChange={(e) => updateField("floor", e.target.value)}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={3}>
                            <FormGroup>
                                <Label>
                                    {roomOptions.length ? "Room" : "Room Name / Number"} (optional)
                                </Label>
                                {roomOptions.length ? (
                                    <Input
                                        type="select"
                                        value={roomValue}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            updateField("room_name_or_number", next);
                                            if (next !== "OTHERS") updateField("room_others", "");
                                        }}
                                    >
                                        <option value="">Select room</option>
                                        {roomOptions.map((r) => (
                                            <option key={r.value} value={r.value}>
                                                {r.label}
                                            </option>
                                        ))}
                                        <option value="OTHERS">Others (Specify)</option>
                                    </Input>
                                ) : (
                                    <Input
                                        value={roomValue}
                                        onChange={(e) =>
                                            updateField("room_name_or_number", e.target.value)
                                        }
                                    />
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    {isBuildingOthers && (
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Specify Building Name</Label>
                                    <Input
                                        value={v(
                                            firstDefinedValue(formValues, [
                                                "other_building_name",
                                                "otherBuildingName",
                                            ])
                                        )}
                                        onChange={(e) =>
                                            updateField("other_building_name", e.target.value)
                                        }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}

                    {isRoomOthers && (
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Specify Room</Label>
                                    <Input
                                        value={v(
                                            firstDefinedValue(formValues, ["room_others", "roomOthers"])
                                        )}
                                        onChange={(e) => updateField("room_others", e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                </>
            )}

            <Row>
                {!isLandCategory && !isBuildingCategory && (
                    <Col md={4}>
                        <FormGroup>
                            <Label>Observed Condition *</Label>
                            <Input
                                type="select"
                                value={observedConditionValue}
                                onChange={(e) =>
                                    ["observed_condition", "observedCondition"].forEach((f) =>
                                        updateField(f, e.target.value)
                                    )
                                }
                            >
                                <option value="">Select condition</option>
                                {observedConditions.map((c) => (
                                    <option key={c.id} value={String(c.id)}>
                                        {c.name}
                                    </option>
                                ))}
                                {observedConditionValue &&
                                    !observedConditions.some((c) => String(c.id) === observedConditionValue) && (
                                        <option value={observedConditionValue}>
                                            {observedConditionValue}
                                        </option>
                                    )}
                            </Input>
                        </FormGroup>
                    </Col>
                )}

                <Col md={4}>
                    <FormGroup>
                        <Label>Acquisition Year</Label>
                        <Input
                            type="select"
                            value={acquisitionYearValue}
                            onChange={(e) => {
                                const val = e.target.value || null;
                                ["acquisition_year", "acquisition_date", "acquisitionDate"].forEach((f) =>
                                    updateField(f, val)
                                );
                            }}
                        >
                            <option value="">Select year</option>
                            {YEAR_OPTIONS.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>

                {isBuildingCategory && (
                    <>
                        {renderInputField("latitude", "Latitude")}
                        {renderInputField("longitude", "Longitude")}
                    </>
                )}

                {!isLandCategory && !isSiteCategory && (
                    <Col md={4}>
                        <FormGroup>
                            <Label>Barcode / Serial</Label>
                            <Input
                                value={v(
                                    firstDefinedValue(formValues, ["barcode", "serial_number", "serialNumber"])
                                )}
                                onChange={(e) => updateField("barcode", e.target.value)}
                            />
                            {isBuildingCategory && toIntOrNull(formValues.number_of_buildings) > 1 && (
                                <div className="small text-muted mt-1" style={{ fontStyle: "italic" }}>
                                    Example sequence for {formValues.number_of_buildings} buildings:{" "}
                                    <strong>
                                        {(() => {
                                            const barcode = firstDefinedValue(formValues, ["barcode", "serial_number", "serialNumber"]) || "";
                                            if (!barcode) return "702001/ACC/507B/OLST/PS/CAB/OS/001, ...002, ...003";
                                            const match = barcode.match(/^(.*?)(\d+)$/);
                                            if (!match) return `${barcode}, ${barcode}-2, ${barcode}-3`;
                                            const prefix = match[1];
                                            const startStr = match[2];
                                            const len = startStr.length;
                                            const startNum = parseInt(startStr, 10);
                                            const examples = [];
                                            const count = toIntOrNull(formValues.number_of_buildings);
                                            for (let i = 0; i < Math.min(count, 3); i++) {
                                                const nextNum = startNum + i;
                                                const nextStr = String(nextNum).padStart(len, "0");
                                                examples.push(prefix + nextStr);
                                            }
                                            if (count > 3) examples.push("...");
                                            return examples.join(", ");
                                        })()}
                                    </strong>
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                )}
            </Row>

            {isInfrastructureCategory && (
                <>
                    <h6 className="text-uppercase text-muted mt-4 mb-3">Infrastructure Details</h6>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Street</Label>
                                <Input
                                    value={v(firstDefinedValue(formValues, ["street"]))}
                                    onChange={(e) => updateField("street", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={8}>
                            <FormGroup>
                                <Label>Type of Bridge</Label>
                                <Input
                                    value={v(firstDefinedValue(formValues, ["type_of_bridge", "typeOfBridge"]))}
                                    onChange={(e) =>
                                        ["type_of_bridge", "typeOfBridge"].forEach((f) =>
                                            updateField(f, e.target.value)
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label>Width (m)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={v(firstDefinedValue(formValues, ["width_m", "widthM"]))}
                                    onChange={(e) =>
                                        ["width_m", "widthM"].forEach((f) => updateField(f, e.target.value))
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>Span (m)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={v(firstDefinedValue(formValues, ["span_m", "spanM"]))}
                                    onChange={(e) =>
                                        ["span_m", "spanM"].forEach((f) => updateField(f, e.target.value))
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>Thickness (m)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={v(firstDefinedValue(formValues, ["thickness_m", "thicknessM"]))}
                                    onChange={(e) =>
                                        ["thickness_m", "thicknessM"].forEach((f) =>
                                            updateField(f, e.target.value)
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>Depth (m)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={v(firstDefinedValue(formValues, ["depth_m", "depthM"]))}
                                    onChange={(e) =>
                                        ["depth_m", "depthM"].forEach((f) => updateField(f, e.target.value))
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Type of Foundation</Label>
                                <Input
                                    value={v(
                                        firstDefinedValue(formValues, [
                                            "type_of_foundation",
                                            "typeOfFoundation",
                                        ])
                                    )}
                                    onChange={(e) =>
                                        ["type_of_foundation", "typeOfFoundation"].forEach((f) =>
                                            updateField(f, e.target.value)
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Deck Type</Label>
                                <Input
                                    value={v(firstDefinedValue(formValues, ["deck_type", "deckType"]))}
                                    onChange={(e) =>
                                        ["deck_type", "deckType"].forEach((f) =>
                                            updateField(f, e.target.value)
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Handrail Type</Label>
                                <Input
                                    value={v(firstDefinedValue(formValues, ["handrail_type", "handrailType"]))}
                                    onChange={(e) =>
                                        ["handrail_type", "handrailType"].forEach((f) =>
                                            updateField(f, e.target.value)
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Number of Steps</Label>
                                <Input
                                    value={v(
                                        firstDefinedValue(formValues, ["number_of_steps", "numberOfSteps"])
                                    )}
                                    onChange={(e) =>
                                        ["number_of_steps", "numberOfSteps"].forEach((f) =>
                                            updateField(f, e.target.value)
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            )}

            {(isVehicleCategory || isEquipmentCategory || isICTCategory || isMachineryCategory) && (
                <>
                    <h6 className="text-uppercase text-muted mt-4 mb-3">Asset Specifications</h6>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Make</Label>
                                <Input
                                    value={v(formValues.make)}
                                    onChange={(e) => updateField("make", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Model</Label>
                                <Input
                                    value={v(formValues.model)}
                                    onChange={(e) => updateField("model", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Year of Manufacture</Label>
                                <Input
                                    type="select"
                                    value={v(
                                        firstDefinedValue(formValues, [
                                            "year_of_manufacture",
                                            "yearOfManufacture",
                                        ])
                                    )}
                                    onChange={(e) =>
                                        ["year_of_manufacture", "yearOfManufacture"].forEach((f) =>
                                            updateField(f, e.target.value)
                                        )
                                    }
                                >
                                    <option value="">Select year</option>
                                    {YEAR_OPTIONS.map((y) => (
                                        <option key={y} value={y}>
                                            {y}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    {(isVehicleCategory || isMachineryCategory) && (
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Chassis Number</Label>
                                    <Input
                                        value={v(
                                            firstDefinedValue(formValues, [
                                                "chassis_number",
                                                "chassisNumber",
                                            ])
                                        )}
                                        onChange={(e) =>
                                            ["chassis_number", "chassisNumber"].forEach((f) =>
                                                updateField(f, e.target.value)
                                            )
                                        }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Engine Number</Label>
                                    <Input
                                        value={v(
                                            firstDefinedValue(formValues, [
                                                "engine_number",
                                                "engineNumber",
                                            ])
                                        )}
                                        onChange={(e) =>
                                            ["engine_number", "engineNumber"].forEach((f) =>
                                                updateField(f, e.target.value)
                                            )
                                        }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Milage (Km)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={v(firstDefinedValue(formValues, ["odo"]))}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            if (value === "") {
                                                updateField("odo", null);
                                                return;
                                            }

                                            const nextValue = Number(value);
                                            if (Number.isNaN(nextValue) || nextValue < 0) {
                                                return;
                                            }

                                            updateField("odo", nextValue);
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            {isVehicleCategory && (
                                <Col md={4}>
                                    <FormGroup>
                                        <Label>Registration Number</Label>
                                        <Input
                                            value={v(
                                                firstDefinedValue(formValues, [
                                                    "registration_number",
                                                    "registrationNumber",
                                                ])
                                            )}
                                            onChange={(e) =>
                                                ["registration_number", "registrationNumber"].forEach((f) =>
                                                    updateField(f, e.target.value)
                                                )
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                            )}
                        </Row>
                    )}
                </>
            )}

            <h6 className="text-uppercase text-muted mt-4 mb-3">Remarks</h6>
            <FormGroup>
                <Input
                    type="textarea"
                    rows={3}
                    value={remarkValue}
                    onChange={(e) => updateField("remark", e.target.value)}
                />
            </FormGroup>

            {isLandCategory && (
                <Row>
                    {renderInputField("latitude", "Latitude")}
                    {renderInputField("longitude", "Longitude")}
                </Row>
            )}

            {isBuildingCategory && (
                <>
                    <h6 className="text-uppercase text-muted mt-4 mb-3">Building Details</h6>
                    <Row>
                        {renderInputField("building_number", "Building Number")}
                        {renderInputField("number_of_buildings", "Number of Buildings", { type: "number" })}
                        <Col md={4}>
                            <FormGroup>
                                <Label>Building Use</Label>
                                <Input
                                    type="select"
                                    value={buildingUseIdValue}
                                    onChange={(e) => {
                                        const selected = buildingUses.find(
                                            (item) => String(item.id) === e.target.value
                                        );
                                        updateField("building_use_id", e.target.value);
                                        updateField("buildingUseId", e.target.value);
                                        updateField("building_use_name", selected?.name || "");
                                        updateField("buildingUseName", selected?.name || "");
                                        updateField("building_use", selected || null);
                                    }}
                                >
                                    <option value="">Select building use</option>
                                    {buildingUses.map((item) => (
                                        <option key={item.id} value={String(item.id)}>
                                            {item.name || item.code || String(item.id)}
                                        </option>
                                    ))}
                                    {buildingUseIdValue &&
                                        !buildingUses.some((item) => String(item.id) === buildingUseIdValue) && (
                                            <option value={buildingUseIdValue}>
                                                {buildingUseValue || buildingUseIdValue}
                                            </option>
                                        )}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        {renderBooleanField("has_water", "Has Water")}
                        {renderBooleanField("has_electricity", "Has Electricity")}
                        {renderInputField("plot_size", "Plot Size")}
                    </Row>
                </>
            )}

            {isLandCategory && (
                <>
                    <h6 className="text-uppercase text-muted mt-4 mb-3">Land Details</h6>
                    <Row>
                        {renderInputField("plot_number", "Plot Number")}
                        {renderInputField("certificate_of_title", "Certificate of Title")}
                        {renderInputField("tenure_years", "Tenure (years)", { type: "number" })}
                    </Row>
                    <Row>
                        {renderInputField("plot_size", "Plot Size")}
                        {renderInputField("size_unit", "Size Unit")}
                        {renderInputField("land_value_per_sqm", "Land Value / Sqm", { type: "number", sourceFields: ["land_value_per_sqm", "landValuePerSqm"] })}
                    </Row>
                    <Row>
                        {renderInputField("measurement_unit", "Measurement Unit")}
                        {renderInputField("measurement", "Measurement Value", { type: "number" })}
                    </Row>
                    <Row>
                        {renderInputField("right_of_occupancy_start", "Right of Occupancy Start", {
                            sourceFields: ["right_of_occupancy_start", "right_of_occupance_start"],
                            writeFields: ["right_of_occupancy_start", "right_of_occupance_start"],
                        })}
                        {renderInputField("right_of_occupancy_end", "Right of Occupancy End", {
                            sourceFields: ["right_of_occupancy_end", "right_of_occupance_end"],
                            writeFields: ["right_of_occupancy_end", "right_of_occupance_end"],
                        })}
                    </Row>
                    <Row>{renderBooleanField("is_land_surveyed", "Land Surveyed")}</Row>
                </>
            )}

            {isSiteCategory && (
                <>
                    <h6 className="text-uppercase text-muted mt-4 mb-3">Site Measurements</h6>
                    <Row>
                        {renderInputField("measurement", "Measurement Value", { type: "number" })}
                        {renderInputField("measurement_unit", "Measurement Unit")}
                        {renderInputField("size_unit", "Size Unit")}
                    </Row>
                </>
            )}
        </div>
        <Modal isOpen={isAssetCategoryConfirmOpen} toggle={() => setIsAssetCategoryConfirmOpen(false)} centered>
            <ModalHeader toggle={() => setIsAssetCategoryConfirmOpen(false)}>
                Confirm Asset Category Change
            </ModalHeader>
            <ModalBody>
                Changing the asset category may affect related fields such as asset name and category-specific details.
                Do you want to continue?
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={() => setIsAssetCategoryConfirmOpen(false)}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={() => {
                        setIsAssetCategoryEditable(true);
                        setIsAssetCategoryConfirmOpen(false);
                    }}
                >
                    Continue
                </Button>
            </ModalFooter>
        </Modal>
        </fieldset>
    );
};

export default AssetDetailForm;
